import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, TrendingUp, DollarSign, MapPin, Building2, BarChart3, Home } from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import SEO, { breadcrumbSchema } from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import { getRankingsByCategory } from '@/data/rankingsData';
import { councils } from '@/data/councilData';

const categoryIcons = {
  Price: DollarSign,
  Growth: TrendingUp,
  Yield: BarChart3,
  Location: MapPin,
  Development: Building2,
  Demand: Home,
};

const categoryColors = {
  Price: '#C4A36B',
  Growth: '#2E7D32',
  Yield: '#1565C0',
  Location: '#6A1B9A',
  Development: '#E65100',
  Demand: '#AD1457',
};

export default function SuburbRankingsIndexPage() {
  const grouped = getRankingsByCategory();

  return (
    <>
      <SEO
        title="Suburb Rankings | Top 10 Lists for SEQ Property"
        description="Browse suburb rankings across South East Queensland. Top 10 lists for price, growth, rental yield, development potential, and more. Data-driven property insights from ACRES."
        url="/rankings"
        schemas={[breadcrumbSchema([{ name: 'Home', url: '/' }, { name: 'Rankings' }])]}
      />
      <Navigation />

      {/* Hero */}
      <section className="bg-[#042E0C] pt-32 pb-16">
        <div className="max-w-5xl mx-auto px-6">
          <Breadcrumbs items={[{ name: 'Suburb Rankings' }]} variant="dark" />
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light text-[#FFF4EA] mb-3" data-testid="rankings-heading">
            Suburb Rankings
          </h1>
          <p className="text-[#FFF4EA]/60 max-w-2xl" data-testid="rankings-subtitle">
            Data-driven Top 10 lists across {Object.values(grouped).flat().length} categories. Compare suburbs by price, growth, yield, development potential, and demand.
          </p>
        </div>
      </section>

      {/* Rankings Grid */}
      <section className="bg-[#FFF4EA] py-16">
        <div className="max-w-5xl mx-auto px-6">
          {Object.entries(grouped).map(([category, items], ci) => {
            const Icon = categoryIcons[category] || TrendingUp;
            const color = categoryColors[category] || '#042E0C';
            return (
              <div key={category} className="mb-12 last:mb-0" data-testid={`category-${category.toLowerCase()}`}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: color }}>
                    <Icon size={18} className="text-white" />
                  </div>
                  <h2 className="font-heading text-2xl font-light text-[#042E0C]">{category}</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {items.map((ranking, i) => (
                    <motion.div
                      key={ranking.slug}
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: (ci * items.length + i) * 0.04 }}
                    >
                      <Link
                        to={`/rankings/${ranking.slug}`}
                        className="block bg-white border border-[#042E0C]/10 p-5 hover:border-[#C4A36B] hover:shadow-sm transition-all group h-full"
                        data-testid={`ranking-card-${ranking.slug}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-heading text-base text-[#042E0C] group-hover:text-[#C4A36B] transition-colors pr-4">
                            {ranking.title}
                          </h3>
                          <ArrowRight size={16} className="text-[#999] group-hover:text-[#C4A36B] transition-colors flex-shrink-0 mt-1" />
                        </div>
                        <p className="text-xs text-[#666] mb-3">{ranking.subtitle}</p>
                        <div className="flex flex-wrap gap-1.5">
                          {ranking.items.slice(0, 3).map((s, si) => (
                            <span key={`${s.name || s.id || 'rank'}-${si}`} className="px-2 py-0.5 bg-[#FFF4EA] text-xs text-[#042E0C]/60">
                              {si + 1}. {s.name}
                            </span>
                          ))}
                        </div>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* By Council Area */}
      <section className="bg-white py-16 border-t border-[#042E0C]/10" data-testid="council-rankings">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 flex items-center justify-center bg-[#042E0C]">
              <MapPin size={18} className="text-white" />
            </div>
            <h2 className="font-heading text-2xl font-light text-[#042E0C]">By Council Area</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {councils.map((c, i) => (
              <motion.div key={c.slug} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                <Link
                  to={`/rankings/council/${c.slug}`}
                  className="flex items-center justify-between bg-[#FFF4EA] border border-[#042E0C]/10 px-5 py-4 hover:border-[#C4A36B] transition-all group"
                  data-testid={`council-card-${c.slug}`}
                >
                  <div>
                    <p className="font-heading text-base text-[#042E0C] group-hover:text-[#C4A36B] transition-colors">Top Suburbs in {c.shortName}</p>
                    <p className="text-xs text-[#999]">{c.keyStats.suburbs} suburbs</p>
                  </div>
                  <ArrowRight size={16} className="text-[#999] group-hover:text-[#C4A36B] transition-colors flex-shrink-0" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Cross-link to Compare tool */}
      <section className="bg-[#FFF4EA] py-12 border-t border-[#042E0C]/10">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h3 className="font-heading text-xl text-[#042E0C] mb-2">Want to compare specific suburbs?</h3>
          <p className="text-sm text-[#666] mb-6">
            Use our side-by-side comparison tool to analyse any two suburbs across 13+ metrics.
          </p>
          <Link
            to="/compare"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#042E0C] text-[#FFF4EA] text-sm font-medium tracking-wide hover:bg-[#0a4a1a] transition-colors"
            data-testid="compare-cta"
          >
            Compare Suburbs <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <Footer />
    </>
  );
}
